/**
 * Question Bank Admin Page - Enterprise Grade
 * 
 * Comprehensive question and template management interface with advanced features:
 * - Multi-view layouts (cards, table, compact)
 * - Advanced filtering and search
 * - Bulk operations
 * - Quick actions and inline editing
 * - Visual question type indicators
 * - Real-time analytics
 */

import React, { useState, useEffect } from 'react'
import { 
  Plus, Search, Filter, Database, FileText, BarChart3, Settings, Edit,
  LayoutGrid, List, Download, Upload, Copy, Trash2, Eye, ChevronDown,
  MessageSquare, Star, ToggleLeft, Calendar, Users, Target, TrendingUp,
  CheckCircle2, AlertCircle, X, SlidersHorizontal, ArrowUpDown, MoreVertical,
  Clock, FileQuestion, Timer, RefreshCw
} from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import questionBankService, { Question, FeedbackTemplate } from '@/services/api/questionBankService'
import QuestionFormDialog from '@/components/admin/question-bank/QuestionFormDialog'
import TemplateFormDialog from '@/components/admin/question-bank/TemplateFormDialog'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { importAllUATData } from '@/data/uat'
import toast from 'react-hot-toast'

// Question type icons mapping
const QUESTION_TYPE_ICONS: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
  'TEXT': { icon: <MessageSquare className="h-4 w-4" />, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  'RATING_SCALE': { icon: <Star className="h-4 w-4" />, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  'LIKERT': { icon: <SlidersHorizontal className="h-4 w-4" />, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  'MULTIPLE_CHOICE': { icon: <Target className="h-4 w-4" />, color: 'text-green-600', bgColor: 'bg-green-50' },
  'MULTI_SELECT': { icon: <CheckCircle2 className="h-4 w-4" />, color: 'text-teal-600', bgColor: 'bg-teal-50' },
  'YES_NO': { icon: <ToggleLeft className="h-4 w-4" />, color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
}

type ViewMode = 'grid' | 'list' | 'compact'

const QuestionBankPage: React.FC = () => {
  // View and tab state
  const [activeTab, setActiveTab] = useState<'questions' | 'templates' | 'analytics'>('questions')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  
  // Data state
  const [questions, setQuestions] = useState<Question[]>([])
  const [templates, setTemplates] = useState<FeedbackTemplate[]>([])
  const [loading, setLoading] = useState(false)
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedContexts, setSelectedContexts] = useState<string[]>([])
  const [showActiveOnly, setShowActiveOnly] = useState(true)
  
  // Selection and bulk operations
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set())
  const [selectedTemplate, setSelectedTemplate] = useState<FeedbackTemplate | undefined>()
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState<FeedbackTemplate | undefined>()
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false)
  // Per-question preview (separate from the template preview above) —
  // shows a single question rendered with its actual form control, so
  // admins can sanity-check rating-scale ranges, Likert option lists, etc.
  const [previewQuestion, setPreviewQuestion] = useState<Question | undefined>()
  const [previewQuestionOpen, setPreviewQuestionOpen] = useState(false)
  // Import dialog state (file picker → parse preview → commit). Used for
  // both questions and templates — the dialog text + commit handler branch
  // on `importEntity`. Same UX, two backends.
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importEntity, setImportEntity] = useState<'questions' | 'templates'>('questions')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importParseResult, setImportParseResult] = useState<{
    valid: any[]
    errors: { index: number; reason: string }[]
  } | null>(null)
  const [importing, setImporting] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<FeedbackTemplate | undefined>()
  const [selectedQuestion, setSelectedQuestion] = useState<Question | undefined>()
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false)
  const [uatImporting, setUatImporting] = useState(false)
  const [exporting, setExporting] = useState(false)
  
  // Stats
  const [stats, setStats] = useState({
    totalQuestions: 0,
    totalTemplates: 0,
    activeQuestions: 0,
    activeTemplates: 0,
    mostUsedContext: 'Matrix Feedback',
    avgCompletionRate: 92
  })

  useEffect(() => {
    loadData()
  }, [activeTab])

  const loadData = async () => {
    setLoading(true)
    try {
      // Every page, not just the first 100: past that, questions and
      // templates were silently missing (HR UAT #15, Oct 2026).
      if (activeTab === 'questions') {
        const response = await questionBankService.listAllQuestions()
        const items = response.items
        const total = response.total
        setQuestions(items)
        setStats(prev => ({
          ...prev,
          totalQuestions: total,
          activeQuestions: items.filter(q => q.is_active).length
        }))
      } else if (activeTab === 'templates') {
        const response = await questionBankService.listAllTemplates()
        const items = response.items
        const total = response.total
        setTemplates(items)
        setStats(prev => ({
          ...prev,
          totalTemplates: total,
          activeTemplates: items.filter(t => t.is_active).length
        }))
      }
    } catch (error) {
      console.error('Error loading data:', error)
      // Reset to empty arrays on error to prevent filter errors
      if (activeTab === 'questions') {
        setQuestions([])
      } else if (activeTab === 'templates') {
        setTemplates([])
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreateQuestion = () => {
    setSelectedQuestion(undefined)
    setQuestionDialogOpen(true)
  }

  const handleEditQuestion = (question: Question) => {
    setSelectedQuestion(question)
    setQuestionDialogOpen(true)
  }

  const handleQuestionSuccess = () => {
    loadData()
  }

  const handleCreateTemplate = () => {
    setSelectedTemplate(undefined)
    setTemplateDialogOpen(true)
  }

  const handleEditTemplate = (template: FeedbackTemplate) => {
    setSelectedTemplate(template)
    setTemplateDialogOpen(true)
  }

  const handleTemplateSuccess = () => {
    loadData()
  }

  const handlePreviewTemplate = (template: FeedbackTemplate) => {
    setPreviewTemplate(template)
    setPreviewDialogOpen(true)
  }

  const handlePreviewQuestion = (question: Question) => {
    setPreviewQuestion(question)
    setPreviewQuestionOpen(true)
  }

  const handleDuplicateQuestion = async (question: Question) => {
    try {
      // No prompt — the copy gets the next code in its category, as a new
      // question does (leadership_Q08)
      const created = await questionBankService.duplicateQuestion(question.id)
      toast.success(`Duplicated as ${created.question_code}`)
      loadData()
    } catch (error: any) {
      console.error('Duplicate failed:', error)
      toast.error(error?.response?.data?.detail || 'Failed to duplicate question')
    }
  }

  const handleDeleteQuestion = async (question: Question) => {
    if (!window.confirm(`Delete question "${question.question_code}"? This cannot be undone.`)) return
    try {
      await questionBankService.deleteQuestion(question.id)
      toast.success(`Deleted ${question.question_code}`)
      loadData()
    } catch (error: any) {
      console.error('Delete failed:', error)
      toast.error(error?.response?.data?.detail || 'Failed to delete question')
    }
  }

  const handleExportQuestions = async () => {
    setExporting(true)
    try {
      const { blob, filename } = await questionBankService.exportQuestions('xlsx')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Question bank exported')
    } catch (error: any) {
      console.error('Export failed:', error)
      toast.error('Failed to export questions')
    } finally {
      setExporting(false)
    }
  }

  const handleImportFilePicked = (file: File) => {
    // No client-side parse — xlsx parsing belongs server-side. We just
    // record the chosen file and surface a "ready to import" preview;
    // the commit step posts the file as multipart/form-data.
    setImportFile(file)
  }

  const handleImportCommit = async () => {
    if (!importFile) return
    setImporting(true)
    try {
      const result = importEntity === 'templates'
        ? await questionBankService.importTemplates(importFile)
        : await questionBankService.importQuestions(importFile)
      const errMsg = result.errors.length > 0 ? `, ${result.errors.length} errors` : ''
      const parts = [
        `${result.created} new`,
        ...(result.updated && result.updated > 0 ? [`${result.updated} updated`] : []),
        ...(result.skipped > 0 ? [`${result.skipped} skipped`] : []),
      ]
      toast.success(`Imported ${parts.join(', ')}${errMsg}`)
      // Capture errors so the dialog can display them after commit
      setImportParseResult({ valid: [], errors: result.errors })
      if (result.errors.length === 0) {
        setImportDialogOpen(false)
        setImportFile(null)
      }
      loadData()
    } catch (error: any) {
      console.error('Import failed:', error)
      toast.error(error?.response?.data?.detail || `Failed to import ${importEntity}`)
    } finally {
      setImporting(false)
    }
  }

  const handleExportTemplates = async () => {
    setExporting(true)
    try {
      const { blob, filename } = await questionBankService.exportTemplates('xlsx')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Templates exported')
    } catch (error: any) {
      console.error('Template export failed:', error)
      toast.error('Failed to export templates')
    } finally {
      setExporting(false)
    }
  }

  const handleDeleteTemplate = (template: FeedbackTemplate) => {
    setTemplateToDelete(template)
    setDeleteConfirmOpen(true)
  }

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return
    
    try {
      await questionBankService.deleteTemplate(templateToDelete.id)
      loadData()
    } catch (error: any) {
      console.error('Error deleting template:', error)
      const message = error.response?.data?.detail || 'Error deleting template. Please try again.'
      alert(message)
    }
  }

  const handleImportUATData = async (updateExisting = false) => {
    setUatImporting(true)
    const mode = updateExisting ? 'Updating' : 'Importing'
    toast.loading(`${mode} UAT questions and templates...`, { id: 'uat-import' })
    
    try {
      const result = await importAllUATData(updateExisting)
      
      if (result.errors.length === 0) {
        const message = updateExisting 
          ? `Updated ${result.questionsUpdated} questions, ${result.templatesUpdated} templates`
          : `Imported ${result.questionsCreated} questions, ${result.templatesCreated} templates`
        toast.success(message, { id: 'uat-import' })
      } else {
        toast.error(
          `${mode} completed with ${result.errors.length} errors. Check console for details.`,
          { id: 'uat-import' }
        )
      }
      
      // Reload data to show updated items
      loadData()
    } catch (error: any) {
      console.error('UAT import failed:', error)
      toast.error(`Failed to ${mode.toLowerCase()} UAT data. Check console for details.`, { id: 'uat-import' })
    } finally {
      setUatImporting(false)
    }
  }

  // Filter questions based on search and filters
  const filteredQuestions = questions.filter(q => {
    const matchesSearch = !searchQuery || 
      q.question_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.question_code.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesType = selectedTypes.length === 0 || selectedTypes.includes(q.question_type)
    const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(q.category)
    const matchesContext = selectedContexts.length === 0 || 
      q.applicable_feedback_contexts?.some(ctx => selectedContexts.includes(ctx))
    const matchesActive = !showActiveOnly || q.is_active
    
    return matchesSearch && matchesType && matchesCategory && matchesContext && matchesActive
  })

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedQuestions(new Set(filteredQuestions.map(q => q.id)))
    } else {
      setSelectedQuestions(new Set())
    }
  }

  const handleSelectQuestion = (questionId: string, checked: boolean) => {
    const newSelection = new Set(selectedQuestions)
    if (checked) {
      newSelection.add(questionId)
    } else {
      newSelection.delete(questionId)
    }
    setSelectedQuestions(newSelection)
  }

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedQuestions)
    if (ids.length === 0) return
    if (!window.confirm(`Delete ${ids.length} question${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return

    const results = await Promise.allSettled(
      ids.map(id => questionBankService.deleteQuestion(id))
    )
    const succeeded = results.filter(r => r.status === 'fulfilled').length
    const failed = results.length - succeeded

    if (failed === 0) {
      toast.success(`Deleted ${succeeded} question${succeeded !== 1 ? 's' : ''}`)
    } else if (succeeded === 0) {
      toast.error(`Failed to delete ${failed} question${failed !== 1 ? 's' : ''}`)
    } else {
      toast.success(`Deleted ${succeeded}, ${failed} failed`)
    }

    setSelectedQuestions(new Set())
    loadData()
  }

  const handleBulkActivate = async () => {
    const ids = Array.from(selectedQuestions)
    if (ids.length === 0) return

    const results = await Promise.allSettled(
      ids.map(id => questionBankService.updateQuestion(id, { is_active: true }))
    )
    const succeeded = results.filter(r => r.status === 'fulfilled').length
    const failed = results.length - succeeded

    if (failed === 0) {
      toast.success(`Activated ${succeeded} question${succeeded !== 1 ? 's' : ''}`)
    } else if (succeeded === 0) {
      toast.error(`Failed to activate ${failed} question${failed !== 1 ? 's' : ''}`)
    } else {
      toast.success(`Activated ${succeeded}, ${failed} failed`)
    }

    setSelectedQuestions(new Set())
    loadData()
  }

  const handleBulkDeactivate = async () => {
    const ids = Array.from(selectedQuestions)
    if (ids.length === 0) return

    const results = await Promise.allSettled(
      ids.map(id => questionBankService.updateQuestion(id, { is_active: false }))
    )
    const succeeded = results.filter(r => r.status === 'fulfilled').length
    const failed = results.length - succeeded

    if (failed === 0) {
      toast.success(`Deactivated ${succeeded} question${succeeded !== 1 ? 's' : ''}`)
    } else if (succeeded === 0) {
      toast.error(`Failed to deactivate ${failed} question${failed !== 1 ? 's' : ''}`)
    } else {
      toast.success(`Deactivated ${succeeded}, ${failed} failed`)
    }

    setSelectedQuestions(new Set())
    loadData()
  }

  const clearFilters = () => {
    setSelectedTypes([])
    setSelectedCategories([])
    setSelectedContexts([])
    setSearchQuery('')
  }

  const activeFiltersCount = selectedTypes.length + selectedCategories.length + selectedContexts.length

  // Get unique types, categories, and contexts from questions for dynamic filters
  const availableTypes = Array.from(new Set(questions.map(q => q.question_type))).sort()
  const availableCategories = Array.from(new Set(questions.map(q => q.category))).sort()
  const availableContexts = Array.from(
    new Set(questions.flatMap(q => q.applicable_feedback_contexts || []))
  ).sort()

  return (
    <div className="space-y-6">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Database className="h-8 w-8 text-blue-600" />
              In-the-Moment Feedback Questions
            </h2>
            <p className="text-slate-600 mt-1">
              Per-request feedback questions and templates (employee-requested, matrix,
              project, peer). Annual upward campaigns are managed under{' '}
              <span className="font-medium">Feedback Campaigns</span>.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-t-4 border-t-blue-500 hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Questions</p>
                  <h3 className="text-3xl font-bold text-slate-900 mt-2">{stats.totalQuestions}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {stats.activeQuestions} active
                    </Badge>
                  </div>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-green-500 hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Templates</p>
                  <h3 className="text-3xl font-bold text-slate-900 mt-2">{stats.totalTemplates}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {stats.activeTemplates} active
                    </Badge>
                  </div>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Database className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-purple-500 hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Most Used</p>
                  <h3 className="text-lg font-bold text-slate-900 mt-2">{stats.mostUsedContext}</h3>
                  <div className="flex items-center gap-1 mt-2 text-xs text-slate-600">
                    <TrendingUp className="h-3 w-3 text-green-600" />
                    <span>45 requests this month</span>
                  </div>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-amber-500 hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Completion Rate</p>
                  <h3 className="text-3xl font-bold text-slate-900 mt-2">{stats.avgCompletionRate}%</h3>
                  <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                    <TrendingUp className="h-3 w-3" />
                    <span>+3% from last month</span>
                  </div>
                </div>
                <div className="h-12 w-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader className="border-b border-slate-200">
            {/* Advanced Toolbar */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                {/* Search and Filters */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search questions, codes, or templates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-16"
                    />
                    {searchQuery && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {filteredQuestions.length}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => setSearchQuery('')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    variant={showFilters || activeFiltersCount > 0 ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                    {activeFiltersCount > 0 && (
                      <Badge variant="secondary" className="ml-2 px-1.5 py-0 text-xs">
                        {activeFiltersCount}
                      </Badge>
                    )}
                  </Button>
                </div>

                {/* View Mode Switcher */}
                <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-1">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="h-8"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="h-8"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'compact' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('compact')}
                    className="h-8"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </div>

                {/* Actions Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleExportQuestions}
                      disabled={exporting}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {exporting ? 'Exporting…' : 'Export Questions'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setImportEntity('questions')
                        setImportFile(null)
                        setImportParseResult(null)
                        setImportDialogOpen(true)
                      }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Import Questions
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleExportTemplates}
                      disabled={exporting}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {exporting ? 'Exporting…' : 'Export Templates'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setImportEntity('templates')
                        setImportFile(null)
                        setImportParseResult(null)
                        setImportDialogOpen(true)
                      }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Import Templates
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>UAT Testing</DropdownMenuLabel>
                    <DropdownMenuItem 
                      onClick={() => handleImportUATData(false)}
                      disabled={uatImporting}
                      className="text-future-blue"
                    >
                      <Database className="h-4 w-4 mr-2" />
                      {uatImporting ? 'Processing...' : 'Import UAT (Create New)'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleImportUATData(true)}
                      disabled={uatImporting}
                      className="text-amber-600"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {uatImporting ? 'Processing...' : 'Update UAT (Sync Changes)'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-700">Filter Options</h4>
                    {activeFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearFilters} className="h-auto py-1 text-xs">
                        <X className="h-3 w-3 mr-1" />
                        Clear all ({activeFiltersCount})
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {/* Question Type Filter */}
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-2 block">
                        Question Type
                        {selectedTypes.length > 0 && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {selectedTypes.length}
                          </Badge>
                        )}
                      </label>
                      <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                        <div className="space-y-2">
                          {availableTypes.map(type => (
                            <label key={type} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                              <Checkbox
                                checked={selectedTypes.includes(type)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedTypes([...selectedTypes, type])
                                  } else {
                                    setSelectedTypes(selectedTypes.filter(t => t !== type))
                                  }
                                }}
                              />
                              <span className="flex-1">{type.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-400">
                                {questions.filter(q => q.question_type === type).length}
                              </span>
                            </label>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Category Filter */}
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-2 block">
                        Category
                        {selectedCategories.length > 0 && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {selectedCategories.length}
                          </Badge>
                        )}
                      </label>
                      <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                        <div className="space-y-2">
                          {availableCategories.map(cat => (
                            <label key={cat} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                              <Checkbox
                                checked={selectedCategories.includes(cat)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedCategories([...selectedCategories, cat])
                                  } else {
                                    setSelectedCategories(selectedCategories.filter(c => c !== cat))
                                  }
                                }}
                              />
                              <span className="flex-1">{cat.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-400">
                                {questions.filter(q => q.category === cat).length}
                              </span>
                            </label>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Feedback Context Filter */}
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-2 block">
                        Feedback Context
                        {selectedContexts.length > 0 && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {selectedContexts.length}
                          </Badge>
                        )}
                      </label>
                      <ScrollArea className="h-48 rounded border border-slate-200 bg-white p-2">
                        <div className="space-y-2">
                          {availableContexts.map(context => (
                            <label key={context} className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                              <Checkbox
                                checked={selectedContexts.includes(context)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedContexts([...selectedContexts, context])
                                  } else {
                                    setSelectedContexts(selectedContexts.filter(c => c !== context))
                                  }
                                }}
                              />
                              <span className="flex-1 text-xs">{context.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-400">
                                {questions.filter(q => q.applicable_feedback_contexts?.includes(context)).length}
                              </span>
                            </label>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Status and Other Filters */}
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs font-medium text-slate-700 mb-2 block">Status</label>
                        <div className="space-y-2 bg-white rounded border border-slate-200 p-3">
                          <label className="flex items-center gap-2 text-sm hover:bg-slate-50 p-1 rounded cursor-pointer">
                            <Checkbox
                              checked={showActiveOnly}
                              onCheckedChange={(checked) => setShowActiveOnly(checked as boolean)}
                            />
                            <span>Active only</span>
                          </label>
                          <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">
                            <div className="flex justify-between">
                              <span>Active:</span>
                              <span className="font-medium">{questions.filter(q => q.is_active).length}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Inactive:</span>
                              <span className="font-medium">{questions.filter(q => !q.is_active).length}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div>
                        <label className="text-xs font-medium text-slate-700 mb-2 block">Results</label>
                        <div className="bg-blue-50 rounded border border-blue-200 p-3 text-center">
                          <div className="text-2xl font-bold text-blue-900">
                            {filteredQuestions.length}
                          </div>
                          <div className="text-xs text-blue-700">
                            of {questions.length} questions
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bulk Operations Bar */}
              {selectedQuestions.size > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedQuestions.size === filteredQuestions.length}
                      onCheckedChange={handleSelectAll}
                    />
                    <span className="text-sm font-medium text-blue-900">
                      {selectedQuestions.size} question{selectedQuestions.size !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handleBulkActivate}>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Activate
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleBulkDeactivate}>
                      <ToggleLeft className="h-4 w-4 mr-2" />
                      Deactivate
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleBulkDelete}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedQuestions(new Set())}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="questions" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Questions ({stats.totalQuestions})
                </TabsTrigger>
                <TabsTrigger value="templates" className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Templates ({stats.totalTemplates})
                </TabsTrigger>
                <TabsTrigger value="analytics" className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Analytics
                </TabsTrigger>
              </TabsList>

              <TabsContent value="questions" className="mt-6">
                <div className="space-y-4">
                  {/* Header with actions */}
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Question Library</h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Showing {filteredQuestions.length} of {questions.length} questions
                      </p>
                    </div>
                    <Button onClick={handleCreateQuestion} className="shadow-sm">
                      <Plus className="h-4 w-4 mr-2" />
                      New Question
                    </Button>
                  </div>

                  {loading ? (
                    <div className="text-center py-16 bg-slate-50 rounded-lg">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-slate-600 mt-4 font-medium">Loading questions...</p>
                    </div>
                  ) : filteredQuestions.length === 0 ? (
                    <div className="text-center py-16 bg-slate-50 rounded-lg">
                      <Database className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        {questions.length === 0 ? 'No questions yet' : 'No matching questions'}
                      </h3>
                      <p className="text-slate-600 mb-6">
                        {questions.length === 0 
                          ? 'Get started by creating your first question'
                          : 'Try adjusting your filters or search query'
                        }
                      </p>
                      {questions.length === 0 ? (
                        <Button onClick={handleCreateQuestion}>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Question
                        </Button>
                      ) : (
                        <Button onClick={clearFilters} variant="outline">
                          <X className="h-4 w-4 mr-2" />
                          Clear Filters
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Grid View */}
                      {viewMode === 'grid' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {filteredQuestions.map((question) => {
                            const typeConfig = QUESTION_TYPE_ICONS[question.question_type] || QUESTION_TYPE_ICONS['TEXT']
                            return (
                              <Card key={question.id} className="hover:shadow-lg transition-all hover:border-blue-200 group">
                                <CardContent className="pt-6">
                                  <div className="flex items-start gap-4">
                                    {/* Selection Checkbox */}
                                    <Checkbox
                                      checked={selectedQuestions.has(question.id)}
                                      onCheckedChange={(checked) => handleSelectQuestion(question.id, checked as boolean)}
                                      className="mt-1"
                                    />
                                    
                                    {/* Question Type Icon */}
                                    <div className={`${typeConfig.bgColor} ${typeConfig.color} p-3 rounded-lg shrink-0`}>
                                      {typeConfig.icon}
                                    </div>
                                    
                                    {/* Question Content */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <Badge variant="outline" className="text-xs font-medium">
                                            {question.question_type.replace(/_/g, ' ')}
                                          </Badge>
                                          <Badge className="text-xs">
                                            {question.category}
                                          </Badge>
                                          {question.is_required && (
                                            <Badge variant="destructive" className="text-xs">
                                              <AlertCircle className="h-3 w-3 mr-1" />
                                              Required
                                            </Badge>
                                          )}
                                          {!question.is_active && (
                                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                                          )}
                                        </div>
                                      </div>
                                      
                                      <h4 className="text-sm font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                        {question.question_text}
                                      </h4>
                                      
                                      <div className="flex items-center gap-4 text-xs text-slate-600 mb-3">
                                        <span className="font-mono bg-slate-100 px-2 py-1 rounded">
                                          {question.question_code}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <BarChart3 className="h-3 w-3" />
                                          {question.usage_count} uses
                                        </span>
                                      </div>

                                      {/* Applicable Contexts */}
                                      {question.applicable_feedback_contexts && question.applicable_feedback_contexts.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-3">
                                          {question.applicable_feedback_contexts.slice(0, 3).map((context) => (
                                            <Badge key={context} variant="secondary" className="text-xs px-2 py-0">
                                              {context.replace(/_/g, ' ')}
                                            </Badge>
                                          ))}
                                          {question.applicable_feedback_contexts.length > 3 && (
                                            <Badge variant="secondary" className="text-xs px-2 py-0">
                                              +{question.applicable_feedback_contexts.length - 3}
                                            </Badge>
                                          )}
                                        </div>
                                      )}

                                      {/* Actions */}
                                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleEditQuestion(question)}
                                          className="h-8 text-xs"
                                        >
                                          <Edit className="h-3 w-3 mr-1" />
                                          Edit
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 text-xs"
                                          onClick={() => handleDuplicateQuestion(question)}
                                        >
                                          <Copy className="h-3 w-3 mr-1" />
                                          Duplicate
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 text-xs"
                                          onClick={() => handlePreviewQuestion(question)}
                                        >
                                          <Eye className="h-3 w-3 mr-1" />
                                          Preview
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                                          onClick={() => handleDeleteQuestion(question)}
                                        >
                                          <Trash2 className="h-3 w-3 mr-1" />
                                          Delete
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          })}
                        </div>
                      )}

                      {/* List View */}
                      {viewMode === 'list' && (
                        <div className="space-y-2">
                          {filteredQuestions.map((question) => {
                            const typeConfig = QUESTION_TYPE_ICONS[question.question_type] || QUESTION_TYPE_ICONS['TEXT']
                            return (
                              <Card key={question.id} className="hover:bg-slate-50 transition-colors">
                                <CardContent className="py-4">
                                  <div className="flex items-center gap-4">
                                    <Checkbox
                                      checked={selectedQuestions.has(question.id)}
                                      onCheckedChange={(checked) => handleSelectQuestion(question.id, checked as boolean)}
                                    />
                                    
                                    <div className={`${typeConfig.bgColor} ${typeConfig.color} p-2 rounded`}>
                                      {typeConfig.icon}
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-3 mb-1">
                                        <span className="font-mono text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                                          {question.question_code}
                                        </span>
                                        <Badge variant="outline" className="text-xs">
                                          {question.question_type.replace(/_/g, ' ')}
                                        </Badge>
                                        <Badge className="text-xs">{question.category}</Badge>
                                        {question.is_required && (
                                          <Badge variant="destructive" className="text-xs">Required</Badge>
                                        )}
                                        {!question.is_active && (
                                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                                        )}
                                      </div>
                                      <p className="text-sm font-medium text-slate-900 line-clamp-1">
                                        {question.question_text}
                                      </p>
                                    </div>

                                    <div className="flex items-center gap-2 text-xs text-slate-600">
                                      <span className="flex items-center gap-1">
                                        <BarChart3 className="h-3 w-3" />
                                        {question.usage_count}
                                      </span>
                                    </div>

                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm">
                                          <MoreVertical className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleEditQuestion(question)}>
                                          <Edit className="h-4 w-4 mr-2" />
                                          Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleDuplicateQuestion(question)}>
                                          <Copy className="h-4 w-4 mr-2" />
                                          Duplicate
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handlePreviewQuestion(question)}>
                                          <Eye className="h-4 w-4 mr-2" />
                                          Preview
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          className="text-red-600"
                                          onClick={() => handleDeleteQuestion(question)}
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          })}
                        </div>
                      )}

                      {/* Compact View */}
                      {viewMode === 'compact' && (
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                          <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 grid grid-cols-12 gap-4 text-xs font-semibold text-slate-600 uppercase">
                            <div className="col-span-1">
                              <Checkbox
                                checked={selectedQuestions.size === filteredQuestions.length && filteredQuestions.length > 0}
                                onCheckedChange={handleSelectAll}
                              />
                            </div>
                            <div className="col-span-2">Type</div>
                            <div className="col-span-1">Code</div>
                            <div className="col-span-4">Question</div>
                            <div className="col-span-2">Category</div>
                            <div className="col-span-1">Uses</div>
                            <div className="col-span-1">Actions</div>
                          </div>
                          <div className="divide-y divide-slate-200">
                            {filteredQuestions.map((question) => {
                              const typeConfig = QUESTION_TYPE_ICONS[question.question_type] || QUESTION_TYPE_ICONS['TEXT']
                              return (
                                <div key={question.id} className="px-4 py-3 grid grid-cols-12 gap-4 items-center hover:bg-slate-50 transition-colors">
                                  <div className="col-span-1">
                                    <Checkbox
                                      checked={selectedQuestions.has(question.id)}
                                      onCheckedChange={(checked) => handleSelectQuestion(question.id, checked as boolean)}
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <div className="flex items-center gap-2">
                                      <div className={`${typeConfig.bgColor} ${typeConfig.color} p-1.5 rounded`}>
                                        {typeConfig.icon}
                                      </div>
                                      <span className="text-xs text-slate-600">
                                        {question.question_type.split('_')[0]}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="col-span-1">
                                    <code className="text-xs text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                                      {question.question_code.split('_')[1]}
                                    </code>
                                  </div>
                                  <div className="col-span-4">
                                    <p className="text-sm text-slate-900 line-clamp-1">
                                      {question.question_text}
                                    </p>
                                  </div>
                                  <div className="col-span-2">
                                    <Badge variant="outline" className="text-xs">
                                      {question.category}
                                    </Badge>
                                  </div>
                                  <div className="col-span-1 text-sm text-slate-600">
                                    {question.usage_count}
                                  </div>
                                  <div className="col-span-1">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                          <MoreVertical className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleEditQuestion(question)}>
                                          <Edit className="h-4 w-4 mr-2" />
                                          Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleDuplicateQuestion(question)}>
                                          <Copy className="h-4 w-4 mr-2" />
                                          Duplicate
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handlePreviewQuestion(question)}>
                                          <Eye className="h-4 w-4 mr-2" />
                                          Preview
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          className="text-red-600"
                                          onClick={() => handleDeleteQuestion(question)}
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="templates" className="mt-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Template Library</h3>
                    <Button size="sm" onClick={handleCreateTemplate}>
                      <Plus className="h-4 w-4 mr-2" />
                      New Template
                    </Button>
                  </div>

                  {/* Template Analytics Summary */}
                  {!loading && templates.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-600">Total Templates</p>
                              <p className="text-2xl font-bold text-slate-900 mt-1">{templates.length}</p>
                            </div>
                            <FileText className="h-8 w-8 text-blue-500" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-600">Total Uses</p>
                              <p className="text-2xl font-bold text-slate-900 mt-1">
                                {templates.reduce((sum, t) => sum + t.usage_count, 0)}
                              </p>
                            </div>
                            <BarChart3 className="h-8 w-8 text-green-500" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-600">Most Popular</p>
                              <p className="text-sm font-semibold text-slate-900 mt-1 truncate max-w-[150px]">
                                {templates.sort((a, b) => b.usage_count - a.usage_count)[0]?.template_name || 'None'}
                              </p>
                              <p className="text-xs text-slate-500">
                                {templates.sort((a, b) => b.usage_count - a.usage_count)[0]?.usage_count || 0} uses
                              </p>
                            </div>
                            <Star className="h-8 w-8 text-amber-500" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-600">Active Templates</p>
                              <p className="text-2xl font-bold text-slate-900 mt-1">
                                {templates.filter(t => t.is_active).length}
                              </p>
                            </div>
                            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {loading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-slate-600 mt-4">Loading templates...</p>
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">No templates yet</h3>
                      <p className="text-slate-600 mb-6">
                        Create your first feedback template
                      </p>
                      <Button onClick={handleCreateTemplate}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Template
                      </Button>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {templates.map((template) => {
                        // Determine if this is a popular template (top 20% usage)
                        const maxUsage = Math.max(...templates.map(t => t.usage_count))
                        const isPopular = template.usage_count > 0 && template.usage_count >= maxUsage * 0.6
                        
                        // Format last used date
                        const lastUsedText = template.last_used_at 
                          ? new Date(template.last_used_at).toLocaleDateString('en-ZA', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })
                          : 'Never used'
                        
                        return (
                        <Card key={template.id} className="hover:shadow-md transition-shadow">
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  {(template.template_type ?? 'in_the_moment') === 'annual_campaign' ? (
                                    <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                                      Annual Campaign
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                                      In-the-Moment
                                    </Badge>
                                  )}
                                  <Badge variant="outline">{template.feedback_context}</Badge>
                                  {template.primary_stakeholder_role && (
                                    <Badge>{template.primary_stakeholder_role}</Badge>
                                  )}
                                  {template.is_default && (
                                    <Badge variant="default">Default</Badge>
                                  )}
                                  {isPopular && (
                                    <Badge className="bg-amber-500 hover:bg-amber-600">
                                      ⭐ Popular
                                    </Badge>
                                  )}
                                  {!template.is_active && (
                                    <Badge variant="secondary">Inactive</Badge>
                                  )}
                                </div>
                                <CardTitle className="text-base">{template.template_name}</CardTitle>
                                <CardDescription className="mt-2">
                                  {template.description || `Code: ${template.template_code}`}
                                </CardDescription>
                                
                                {/* Analytics Stats */}
                                <div className="flex items-center gap-4 mt-3 text-xs text-slate-600">
                                  <div className="flex items-center gap-1.5">
                                    <BarChart3 className="h-3.5 w-3.5" />
                                    <span className="font-medium">{template.usage_count}</span>
                                    <span>uses</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span>Last: {lastUsedText}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <FileQuestion className="h-3.5 w-3.5" />
                                    <span>{template.template_questions?.length || 0} questions</span>
                                  </div>
                                  {template.estimated_completion_time && (
                                    <div className="flex items-center gap-1.5">
                                      <Timer className="h-3.5 w-3.5" />
                                      <span>~{template.estimated_completion_time} min</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handlePreviewTemplate(template)}
                                >
                                  Preview
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleEditTemplate(template)}
                                >
                                  Edit
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteTemplate(template)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                        )
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="analytics" className="mt-6">
                <div className="text-center py-12">
                  <BarChart3 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Analytics Dashboard</h3>
                  <p className="text-slate-600">
                    Question and template usage analytics coming soon
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Question Form Dialog */}
      <QuestionFormDialog
        open={questionDialogOpen}
        onOpenChange={setQuestionDialogOpen}
        question={selectedQuestion}
        onSuccess={handleQuestionSuccess}
      />

      {/* Template Form Dialog */}
      <TemplateFormDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        template={selectedTemplate}
        onSuccess={handleTemplateSuccess}
      />

      {/* Template Preview Dialog - Realistic Form View */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-600" />
              {previewTemplate?.template_name}
            </DialogTitle>
            <DialogDescription>
              Preview how this template appears when filling out feedback
            </DialogDescription>
          </DialogHeader>

          {previewTemplate && (
            <div className="space-y-6">
              {/* Template Header - As seen by respondent */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{previewTemplate.template_name}</h3>
                    <p className="text-sm text-slate-600 mt-1">{previewTemplate.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-white">
                      {previewTemplate.feedback_context.replace(/_/g, ' ')}
                    </Badge>
                    {previewTemplate.estimated_completion_time && (
                      <Badge variant="secondary" className="bg-white">
                        ~{previewTemplate.estimated_completion_time} min
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-600">
                  <div className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    {previewTemplate.template_questions?.length || 0} questions
                  </div>
                  {(previewTemplate.template_questions?.filter(tq => tq.is_required).length ?? 0) > 0 && (
                    <div className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 text-red-500" />
                      {previewTemplate.template_questions?.filter(tq => tq.is_required).length} required
                    </div>
                  )}
                </div>
              </div>

              {/* Questions - As they appear in the form */}
              <ScrollArea className="h-[500px]">
                <div className="space-y-6 pr-4">
                  {previewTemplate.template_questions?.map((tq, index) => (
                    <div key={tq.id} className="bg-white border border-slate-200 rounded-lg p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-sm font-medium text-slate-900">
                              {tq.custom_question_text || tq.question.question_text}
                              {tq.is_required && <span className="text-red-500 ml-1">*</span>}
                            </h4>
                            <Badge variant="outline" className="text-xs ml-2">
                              {tq.question.question_type.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          {(tq.custom_helper_text || tq.question.helper_text) && (
                            <p className="text-xs text-slate-500 mb-3">
                              {tq.custom_helper_text || tq.question.helper_text}
                            </p>
                          )}
                          
                          {/* Show actual form controls based on question type */}
                          <div className="mt-3">
                            {tq.question.question_type.toUpperCase() === 'TEXT' && (
                              <Input
                                className="w-full"
                                placeholder="Click to enter your feedback..."
                                disabled
                              />
                            )}
                            {tq.question.question_type.toUpperCase() === 'RATING_SCALE' && (() => {
                              // Read the configured min / max / labels from
                              // the question_config — falling back to a 1–5
                              // English-labelled scale only when nothing is
                              // configured. The previous implementation
                              // hardcoded 1–5 regardless, so a question
                              // configured with max=10 still rendered five
                              // buttons in preview.
                              const cfg = tq.question.question_config || {}
                              const min = typeof cfg.min === 'number' ? cfg.min : 1
                              const max = typeof cfg.max === 'number' ? cfg.max : 5
                              const range = max - min + 1
                              const numbers = Array.from({ length: Math.max(range, 1) }, (_, i) => min + i)
                              const labels: Record<number, string> = cfg.labels || {}
                              // Fallback English labels only when no custom
                              // labels are supplied AND we're at the canonical
                              // 1–5 scale — otherwise show no label rather
                              // than misleading text.
                              const isCanonical = min === 1 && max === 5 && Object.keys(labels).length === 0
                              const fallback = (n: number) =>
                                n === 1 ? 'Poor' : n === 2 ? 'Fair' : n === 3 ? 'Good' : n === 4 ? 'Very Good' : 'Excellent'
                              return (
                                <div className="space-y-2">
                                  <div className={`flex items-center ${range > 7 ? 'gap-1' : 'justify-between'} flex-wrap`}>
                                    {numbers.map(n => (
                                      <button
                                        key={n}
                                        className="flex flex-col items-center gap-2 p-3 rounded-lg border-2 border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                                        disabled
                                      >
                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-lg font-semibold text-slate-600">
                                          {n}
                                        </div>
                                        {(labels[n] || (isCanonical && fallback(n))) && (
                                          <span className="text-xs text-slate-500">
                                            {labels[n] || fallback(n)}
                                          </span>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )
                            })()}
                            {tq.question.question_type.toUpperCase() === 'YES_NO' && (
                              <div className="flex gap-4">
                                <label className="flex items-center gap-3 px-6 py-3 border-2 border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors">
                                  <input type="radio" name={`preview-${tq.id}`} className="w-4 h-4" disabled />
                                  <span className="text-sm font-medium text-slate-700">Yes</span>
                                </label>
                                <label className="flex items-center gap-3 px-6 py-3 border-2 border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors">
                                  <input type="radio" name={`preview-${tq.id}`} className="w-4 h-4" disabled />
                                  <span className="text-sm font-medium text-slate-700">No</span>
                                </label>
                              </div>
                            )}
                            {tq.question.question_type.toUpperCase() === 'LIKERT' && (
                              <div className="space-y-2">
                                {(tq.question.question_config?.options || ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']).map((option, idx) => (
                                  <label key={idx} className="flex items-center gap-3 px-4 py-3 border-2 border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors">
                                    <input type="radio" name={`preview-${tq.id}`} className="w-4 h-4" disabled />
                                    <span className="text-sm text-slate-700">{option}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                            {tq.question.question_type.toUpperCase() === 'MULTIPLE_CHOICE' && (
                              <div className="space-y-2">
                                {(tq.question.question_config?.options || ['Option 1', 'Option 2', 'Option 3', 'Option 4']).map((option, idx) => (
                                  <label key={idx} className="flex items-center gap-3 px-4 py-3 border-2 border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors">
                                    <input type="radio" name={`preview-${tq.id}`} className="w-4 h-4" disabled />
                                    <span className="text-sm text-slate-700">{option}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                            {tq.question.question_type.toUpperCase() === 'MULTI_SELECT' && (
                              <div className="space-y-2">
                                {(tq.question.question_config?.options || ['Option 1', 'Option 2', 'Option 3', 'Option 4']).map((option, idx) => (
                                  <label key={idx} className="flex items-center gap-3 px-4 py-3 border-2 border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors">
                                    <input type="checkbox" className="w-4 h-4" disabled />
                                    <span className="text-sm text-slate-700">{option}</span>
                                  </label>
                                ))}
                                {tq.question.question_config?.min_selections !== undefined && (
                                  <p className="text-xs text-slate-500 mt-2">
                                    Select {tq.question.question_config.min_selections > 0 ? `at least ${tq.question.question_config.min_selections}` : 'all that apply'}
                                    {tq.question.question_config.max_selections && ` (max ${tq.question.question_config.max_selections})`}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Footer Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-xs text-slate-500">
                  Used {previewTemplate.usage_count} times • Created {new Date(previewTemplate.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close Preview
            </Button>
            <Button onClick={() => {
              setPreviewDialogOpen(false)
              handleEditTemplate(previewTemplate!)
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-question preview dialog. Renders the actual form control so
          admins can sanity-check rating scales, Likert option lists,
          MULTI_SELECT min/max, etc. — without having to open the question
          inside a template. */}
      <Dialog open={previewQuestionOpen} onOpenChange={setPreviewQuestionOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Question Preview
            </DialogTitle>
            <DialogDescription>
              How this question appears when answering feedback
            </DialogDescription>
          </DialogHeader>

          {previewQuestion && (() => {
            const q = previewQuestion
            const cfg: any = q.question_config || {}
            const type = q.question_type.toUpperCase()
            return (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <code className="bg-slate-100 text-slate-700 px-2 py-1 rounded font-mono">
                    {q.question_code}
                  </code>
                  <Badge variant="outline">{type.replace(/_/g, ' ')}</Badge>
                  <Badge>{q.category}</Badge>
                  {q.is_required && <Badge variant="destructive">Required</Badge>}
                  {!q.is_active && <Badge variant="secondary">Inactive</Badge>}
                </div>

                <div className="border border-slate-200 rounded-lg p-5 bg-white">
                  <h4 className="text-sm font-medium text-slate-900 mb-2">
                    {q.question_text}
                    {q.is_required && <span className="text-red-500 ml-1">*</span>}
                  </h4>
                  {q.helper_text && (
                    <p className="text-xs text-slate-500 mb-3">{q.helper_text}</p>
                  )}

                  <div className="mt-3">
                    {type === 'TEXT' && (
                      <Input placeholder="Click to enter your feedback..." disabled />
                    )}

                    {type === 'RATING_SCALE' && (() => {
                      const min = typeof cfg.min === 'number' ? cfg.min : 1
                      const max = typeof cfg.max === 'number' ? cfg.max : 5
                      const range = max - min + 1
                      const numbers = Array.from({ length: Math.max(range, 1) }, (_, i) => min + i)
                      const labels: Record<number, string> = cfg.labels || {}
                      const isCanonical = min === 1 && max === 5 && Object.keys(labels).length === 0
                      const fallback = (n: number) =>
                        n === 1 ? 'Poor' : n === 2 ? 'Fair' : n === 3 ? 'Good' : n === 4 ? 'Very Good' : 'Excellent'
                      return (
                        <div className={`flex items-center ${range > 7 ? 'gap-1' : 'justify-between'} flex-wrap`}>
                          {numbers.map(n => (
                            <button
                              key={n}
                              className="flex flex-col items-center gap-2 p-3 rounded-lg border-2 border-slate-200"
                              disabled
                            >
                              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-lg font-semibold text-slate-600">
                                {n}
                              </div>
                              {(labels[n] || (isCanonical && fallback(n))) && (
                                <span className="text-xs text-slate-500">{labels[n] || fallback(n)}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )
                    })()}

                    {type === 'YES_NO' && (
                      <div className="flex gap-4">
                        <label className="flex items-center gap-3 px-6 py-3 border-2 border-slate-200 rounded-lg">
                          <input type="radio" name={`preview-q-${q.id}`} className="w-4 h-4" disabled />
                          <span className="text-sm font-medium text-slate-700">Yes</span>
                        </label>
                        <label className="flex items-center gap-3 px-6 py-3 border-2 border-slate-200 rounded-lg">
                          <input type="radio" name={`preview-q-${q.id}`} className="w-4 h-4" disabled />
                          <span className="text-sm font-medium text-slate-700">No</span>
                        </label>
                      </div>
                    )}

                    {(type === 'LIKERT' || type === 'MULTIPLE_CHOICE') && (
                      <div className="space-y-2">
                        {(cfg.options || ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']).map((option: string, idx: number) => (
                          <label key={idx} className="flex items-center gap-3 px-4 py-3 border-2 border-slate-200 rounded-lg">
                            <input type="radio" name={`preview-q-${q.id}`} className="w-4 h-4" disabled />
                            <span className="text-sm text-slate-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {type === 'MULTI_SELECT' && (
                      <div className="space-y-2">
                        {(cfg.options || ['Option 1', 'Option 2', 'Option 3', 'Option 4']).map((option: string, idx: number) => (
                          <label key={idx} className="flex items-center gap-3 px-4 py-3 border-2 border-slate-200 rounded-lg">
                            <input type="checkbox" className="w-4 h-4" disabled />
                            <span className="text-sm text-slate-700">{option}</span>
                          </label>
                        ))}
                        {(cfg.min_selections !== undefined || cfg.max_selections) && (
                          <p className="text-xs text-slate-500 mt-2">
                            Select {cfg.min_selections > 0 ? `at least ${cfg.min_selections}` : 'all that apply'}
                            {cfg.max_selections && ` (max ${cfg.max_selections})`}
                          </p>
                        )}
                      </div>
                    )}

                    {type === 'RANKING' && (
                      <div className="space-y-2">
                        {(cfg.options || ['Item A', 'Item B', 'Item C']).map((option: string, idx: number) => (
                          <div key={idx} className="flex items-center gap-3 px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50">
                            <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-700">
                              {idx + 1}
                            </span>
                            <span className="text-sm text-slate-700">{option}</span>
                          </div>
                        ))}
                        <p className="text-xs text-slate-500 mt-2">
                          Drag to reorder by preference
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {q.applicable_feedback_contexts && q.applicable_feedback_contexts.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-600 uppercase mb-1">Applies to</p>
                    <div className="flex flex-wrap gap-1">
                      {q.applicable_feedback_contexts.map(c => (
                        <Badge key={c} variant="secondary" className="text-xs">
                          {c.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-slate-500">
                  Used {q.usage_count} times • Created {new Date(q.created_at).toLocaleDateString()}
                </p>
              </div>
            )
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewQuestionOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setPreviewQuestionOpen(false)
              if (previewQuestion) handleEditQuestion(previewQuestion)
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Questions dialog. Pick xlsx (or json) file → backend
          parses, validates, inserts. The server is the source of truth
          for parse + validation; the dialog shows whatever errors the
          backend returns after commit (per-row, so one bad row doesn't
          kill the whole import). Re-importing an export is safe — the
          backend skips entries whose code already exists. */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        if (!importing) {
          setImportDialogOpen(open)
          if (!open) {
            setImportFile(null)
            setImportParseResult(null)
          }
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {importEntity === 'templates' ? 'Import Templates' : 'Import Questions'}
            </DialogTitle>
            <DialogDescription>
              {importEntity === 'templates'
                ? 'Upload a two-sheet Excel file (Templates + TemplateQuestions) — typically one you exported earlier. Existing templates (matched by code) are skipped. Question references are resolved by question_code; unknown codes are reported per row but do not abort the rest of the import.'
                : 'Upload an Excel file (xlsx) — typically one you exported earlier and edited. Questions whose code matches an existing entry are UPDATED in place; new rows are created. Re-importing an edited export is the way to bulk-update. JSON files are also accepted.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="import-file">File</Label>
              <Input
                id="import-file"
                type="file"
                accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json,.json"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) handleImportFilePicked(f)
                }}
                disabled={importing}
                className="mt-1"
              />
              {importFile && (
                <p className="text-xs text-slate-500 mt-1">
                  {importFile.name} • {(importFile.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <p className="font-medium text-slate-700 mb-1">Expected format</p>
              {importEntity === 'templates' ? (
                <p>
                  Two sheets: <code className="bg-white px-1 rounded">Templates</code> (one row
                  per template — code, name, context, applicability) and{' '}
                  <code className="bg-white px-1 rounded">TemplateQuestions</code> (links by{' '}
                  <code className="bg-white px-1 rounded">template_code</code> +{' '}
                  <code className="bg-white px-1 rounded">question_code</code>). Encode arrays
                  with semicolons (e.g. <code className="bg-white px-1 rounded">CAMPUS_A;CAMPUS_B</code>).
                </p>
              ) : (
                <p>
                  Leave <code className="bg-white px-1 rounded">question_code</code> blank to
                  auto-generate. Encode arrays with semicolons (e.g.{' '}
                  <code className="bg-white px-1 rounded">MATRIX;PEER</code>); RATING_SCALE
                  labels as{' '}
                  <code className="bg-white px-1 rounded">1:Poor;5:Excellent</code>.
                </p>
              )}
            </div>

            {importParseResult && importParseResult.errors.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-900 font-medium">Errors during import</span>
                  <span className="font-semibold text-amber-700">
                    {importParseResult.errors.length}
                  </span>
                </div>
                <details className="text-xs text-amber-800" open>
                  <summary className="cursor-pointer hover:underline">View details</summary>
                  <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {importParseResult.errors.slice(0, 50).map((e, i) => (
                      <li key={i} className="font-mono">
                        Row #{e.index + 2}: {e.reason}
                      </li>
                    ))}
                    {importParseResult.errors.length > 50 && (
                      <li className="italic">… and {importParseResult.errors.length - 50} more</li>
                    )}
                  </ul>
                </details>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false)
                setImportFile(null)
                setImportParseResult(null)
              }}
              disabled={importing}
            >
              {importParseResult ? 'Close' : 'Cancel'}
            </Button>
            <Button
              onClick={handleImportCommit}
              disabled={!importFile || importing}
            >
              {importing ? 'Importing…' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteTemplate}
        title="Delete Template"
        message={`Are you sure you want to delete "${templateToDelete?.template_name}"?\n\nThis action cannot be undone.${(templateToDelete?.usage_count ?? 0) > 0 ? '\n\n⚠️ This template has been used ' + templateToDelete!.usage_count + ' times and cannot be deleted. Consider deactivating it instead.' : ''}`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default QuestionBankPage
